/* eslint-disable @next/next/no-img-element */
import { Dispatch, FC, SetStateAction, useCallback, useEffect } from 'react';

import { useRouter } from 'next/router';
import Link from 'next/link';

import { SatoshiSVG } from '~/modules/satoshi/components/SatoshiSVG';

import { useAuth } from '~/common/providers/auth';
import { UserSubscriptionTypes } from '~/common/enums/UserSubscriptionTypes';
import { Typography } from '~/common/components/Typography';
import { LabelWrapper } from '~/common/components/TabMenu/Vertical/styles';
import { GenericMenuItem } from '~/common/components/GenericMenuItem';

import {
  FavoritesWrapper,
  SatoshiOptionWrapper,
  SidebarContainer,
  SidebarFooter,
} from './styles';
import { SidebarMenu } from './Menu';
import { SidebarHeader } from './Header';
import { SidebarFavorites } from './Favorites';
import { useSidebar } from '../../providers/sidebar';
import { usePrivateLayout } from '../../providers/privateLayout';
import augusto from '../../../../../public/images/augusto.jpeg';

export interface ISidebarProps {
  closeMobileSidebar?: () => void;
  setOpenMobileAugustoPostsModal?: Dispatch<SetStateAction<boolean>>;
}

export const Sidebar: FC<ISidebarProps> = ({
  closeMobileSidebar,
  setOpenMobileAugustoPostsModal,
}) => {
  const { isMobile, handleOpenMobileSatoshiDrawer } = usePrivateLayout();
  const { collapsed } = useSidebar();
  const { asPath } = useRouter();
  const { logout, user: { subscription } = {} } = useAuth();

  const showSubscription =
    !subscription || subscription === UserSubscriptionTypes.free;

  const handleMobileSatoshiDrawerOpen = useCallback(() => {
    if (closeMobileSidebar) {
      closeMobileSidebar();
      setTimeout(() => {
        handleOpenMobileSatoshiDrawer();
      }, 150);
    }
  }, [closeMobileSidebar, handleOpenMobileSatoshiDrawer]);

  const handleOpenMobileAugustoPostsModal = (): void => {
    closeMobileSidebar();
    setTimeout(() => {
      setOpenMobileAugustoPostsModal?.(true);
    }, 150);
  };

  useEffect(() => {
    if (closeMobileSidebar) {
      closeMobileSidebar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asPath]);

  return (
    <SidebarContainer collapsed={collapsed}>
      <SidebarHeader closeMobileSidebar={closeMobileSidebar} />
      <SidebarMenu />
      <FavoritesWrapper collapsed={collapsed}>
        <LabelWrapper
          id="favorites-label-wrapper"
          style={{
            opacity: collapsed ? 0 : 1,
            transition: '200ms',
            pointerEvents: collapsed ? 'none' : 'auto',
          }}
        >
          <Typography.Subheading size="x-small" color="neutral.400">
            Favoritos
          </Typography.Subheading>
        </LabelWrapper>
        <div style={{ flex: 1, minHeight: 0 }}>
          <SidebarFavorites closeMobileSidebar={closeMobileSidebar} />
        </div>
      </FavoritesWrapper>

      <SidebarFooter>
        {closeMobileSidebar && (
          <GenericMenuItem
            id="posts-augusto"
            selected={asPath === '/posts-augusto'}
            imageUrl={augusto.src}
            title="Posts do Augusto"
            compact={collapsed}
            index={1}
            onClick={handleOpenMobileAugustoPostsModal}
          />
        )}

        {isMobile && (
          <GenericMenuItem
            id="satoshi"
            selected={false}
            title="Satoshi AI"
            compact={collapsed}
            onClick={handleMobileSatoshiDrawerOpen}
            index={2}
            renderContent={() => (
              <SatoshiOptionWrapper>
                <SatoshiSVG />
                <Typography.Label size="small" color="neutral.400">
                  Satoshi AI
                </Typography.Label>
              </SatoshiOptionWrapper>
            )}
          />
        )}

        <Link href="/training" passHref>
          <GenericMenuItem
            id="training"
            selected={asPath === '/training'}
            icon="video-line"
            title="Treinamentos"
            compact={collapsed}
            index={3}
          />
        </Link>

        <Link href="/config" passHref>
          <GenericMenuItem
            id="config"
            selected={asPath === '/config'}
            icon="settings-5-line"
            title="Configurações"
            compact={collapsed}
            index={4}
          />
        </Link>

        {showSubscription && (
          <Link href="/subscription" passHref>
            <GenericMenuItem
              id="subscription"
              selected={asPath === '/subscription'}
              icon="vip-crown-line"
              title="Assinaturas"
              compact={collapsed}
              index={5}
            />
          </Link>
        )}

        <GenericMenuItem
          selected={false}
          icon="logout-box-line"
          title="Sair"
          compact={collapsed}
          index={6}
          onClick={logout}
          iconColor="red.500"
        />
      </SidebarFooter>
    </SidebarContainer>
  );
};
